import React, { useCallback, useState, useEffect } from 'react';
import Tooltip from 'components/tooltip';
import { Col, Row } from 'react-bootstrap-5';

import GenericList from './generic_list';

import { get } from 'helpers/api';
import { debounce } from 'lodash';
import qs from 'qs';

interface Team {
  id: number;
  name: string;
  externalId: string;
  userIds: number[];
  userNames?: string[];
}

interface TeamsListProps {
  companyId?: number;
  selectedTeamIds: number[];
  onAddTeam?: (id: number) => void;
  onRemoveTeam?: (id: number) => void;
}

export default function TeamsList({
  companyId,
  selectedTeamIds,
  onAddTeam,
  onRemoveTeam
}: TeamsListProps) {
  return (
    <Row>
      <Col md={6}>
        <div className="my-3">
          <strong className="fw-semibold">All Available Teams</strong>
        </div>
        <TeamList companyId={companyId} excludeTeamIds={selectedTeamIds || []} onAddTeam={onAddTeam}/>
      </Col>
      <Col md={6}>
        <div className="my-3">
          <strong className="fw-semibold">Teams with Access</strong>
        </div>
        <TeamList companyId={companyId} includeTeamIds={selectedTeamIds || []} onRemoveTeam={onRemoveTeam}/>
      </Col>
    </Row>
  );
}

interface TeamListProps {
  companyId?: number;
  includeTeamIds?: number[];
  excludeTeamIds?: number[];
  onAddTeam?: (id: number) => void;
  onRemoveTeam?: (id: number) => void;
}

function TeamList({
  companyId,
  includeTeamIds,
  excludeTeamIds,
  onAddTeam,
  onRemoveTeam
}: TeamListProps) {
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [teams, setTeams] = useState<Team[]>([]);

  const searchTeams = useCallback(
    debounce((_searchText: string, _companyId?: number, _includeTeamIds?: number[], _excludeTeamIds?: number[]) => {
      const params: { limit: number, [key: string]: string | number | boolean | null } = { limit: 15 };
      if (_searchText) params['q[name_cont]'] = _searchText;
      if (_companyId) params['company_id'] = _companyId;
      let queryString = qs.stringify(params);

      if (_includeTeamIds) _includeTeamIds.forEach((id: number) => {
        queryString += `&q[id_in][]=${id}`;
      });

      if (_excludeTeamIds) _excludeTeamIds.forEach((id: number) => {
        queryString += `&q[id_not_in][]=${id}`;
      });

      get(`/corporate/company_teams?${queryString}`).then((data: Team[]) => {
        setTeams(data);
        setLoading(false);
      });
    }, 400),
    []
  );

  useEffect(() => {
    if (onRemoveTeam && !includeTeamIds?.length) {
      setTeams([]);
      return;
    }

    setLoading(true);
    searchTeams(searchText, companyId, includeTeamIds, excludeTeamIds);
  }, [searchText, companyId, includeTeamIds, excludeTeamIds]);

  const emptyText = () => {
    if (loading) return 'Loading...';

    // if (onRemoveUser && includeUserIds?.length === 0) return 'Start by adding teams';

    return 'No teams Found';
  };

  const userList = ({ userNames }: { userNames?: string[] }) => {
    if (!userNames?.length) return null;

    const text = userNames.join(', ');
    return (
      <Tooltip content={text} dark>
        <div className="text-muted fs-small text-truncate">{text}</div>
      </Tooltip>
    );
  };
  
  return (
    <GenericList
      emptyText={emptyText()}
      items={teams}
      loading={loading}
      renderItemLabel={team => (
        <div>
          <div>{team.name}</div>
          {userList({ userNames: team.userNames })}
        </div>
      )}
      searchText={searchText}
      onAddItem={onAddTeam}
      onRemoveItem={onRemoveTeam}
      onSetSearchText={setSearchText}
    />
  );
}
